<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  analysisLoading: {
    type: Boolean,
    default: false,
  },
  hasFetched: {
    type: Boolean,
    default: false,
  },
  openBillrunCount: {
    type: Number,
    default: 0,
  },
  maxDateRangeCount: {
    type: Number,
    default: 0,
  },
  detailRowCount: {
    type: Number,
    default: 0,
  },
  isDataFullyLoaded: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const dateColumnIndices = computed(() =>
  Array.from({ length: props.maxDateRangeCount }, (_, i) => i + 1)
);

function problemTypeLabel(row) {
  if (row.problemTypeI18nKey) {
    return t(`billrunsDetails.${row.problemTypeI18nKey}`);
  }
  return row.problemType ?? '';
}
</script>

<template>
  <p
    v-if="!hasFetched && !loading"
    class="text-medium-emphasis"
  >
    {{ t('billrunsDashboard.pressGetDataToLoad') }}
  </p>

  <p
    v-else-if="hasFetched && !loading && openBillrunCount === 0"
    class="text-medium-emphasis"
  >
    {{ t('billrunsDashboard.noBillrunsFound') }}
  </p>

  <div v-else-if="hasFetched && (loading || analysisLoading)">
    <v-progress-linear
      indeterminate
      color="primary"
      class="mb-4"
    />
  </div>

  <p
    v-else-if="isDataFullyLoaded && detailRowCount === 0"
    class="text-medium-emphasis"
  >
    {{ t('billrunsDetails.noIssuesFound') }}
  </p>

  <p
    v-else-if="isDataFullyLoaded && rows.length === 0"
    class="text-medium-emphasis"
  >
    {{ t('billrunsDashboard.noBillrunsFound') }}
  </p>

  <div
    v-else-if="isDataFullyLoaded && rows.length > 0"
    class="table-scroll-wrapper"
  >
    <v-table density="compact" class="details-table">
      <thead>
        <tr>
          <th>{{ t('billrunsDetails.columnDso') }}</th>
          <th>{{ t('billrunsDetails.columnCommunity') }}</th>
          <th>{{ t('billrunsDetails.columnMeteringPoint') }}</th>
          <th>{{ t('billrunsDetails.columnProblemType') }}</th>
          <th>{{ t('billrunsDetails.columnMeterCodes') }}</th>
          <th>{{ t('billrunsDetails.columnLastUpdate') }}</th>
          <th
            v-for="n in dateColumnIndices"
            :key="n"
          >
            {{ t('billrunsDetails.columnDate', { n }) }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in rows"
          :key="`${row.ecid}-${row.meteringPoint}-${row.problemType}-${index}`"
        >
          <td>{{ row.dsoDisplay }}</td>
          <td>{{ row.communityDisplay }}</td>
          <td>{{ row.meteringPoint }}</td>
          <td>{{ problemTypeLabel(row) }}</td>
          <td>{{ row.meterCodes }}</td>
          <td>{{ row.lastUpdate }}</td>
          <td
            v-for="n in dateColumnIndices"
            :key="n"
          >
            {{ row.dateRanges[n - 1] ?? '' }}
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
}

.details-table :deep(th) {
  white-space: nowrap;
}
</style>
